/**
 * Addresses and Canadian sales tax.
 *
 * Countries, provinces/states, `normalizeRegion`, `normalizePostalCode` and the labels live in
 * `@buildbee/contracts` (the zod address schema needs them, and contracts cannot depend on
 * domain); they are re-exported here so domain code and its callers have one import.
 */
import {
  countryName,
  DEFAULT_COUNTRY,
  isSupportedCountry,
  normalizePostalCode,
  normalizeRegion,
  type CanadianProvince,
} from '@buildbee/contracts';

export {
  ADDRESS_MESSAGES,
  CA_PROVINCES,
  countryName,
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  DEFAULT_TIMEZONE,
  isSupportedCountry,
  normalizeCountry,
  normalizePostalCode,
  normalizeRegion,
  POSTAL_LABEL,
  postalLabel,
  REGION_LABEL,
  regionLabel,
  regionName,
  regionsFor,
  SUPPORTED_COUNTRIES,
  SUPPORTED_COUNTRY_CODES,
  US_STATES,
  type CanadianProvince,
  type Region,
  type SupportedCountry,
  type UsState,
} from '@buildbee/contracts';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export type AddressLike = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

/**
 * Mailing lines for an address.
 *
 * - Canada (Canada Post): `Toronto ON  M5V 2T6` (province code, two spaces, postal code).
 * - United States (USPS): `Seattle, WA 98101`.
 * - Elsewhere: `City, Region Postal`.
 *
 * Province/state names and postal codes are normalized when they can be. The country name is
 * added as the last line when the address is outside `homeCountry` (default Canada), so domestic
 * mail stays short and cross-border mail is complete.
 */
export function formatAddressLines(address: AddressLike | null | undefined, opts: { homeCountry?: string } = {}): string[] {
  if (!address) return [];
  const country = (address.country?.trim() || DEFAULT_COUNTRY).toUpperCase();
  const city = address.city?.trim() ?? '';
  const regionRaw = address.region?.trim() ?? '';
  const postalRaw = address.postalCode?.trim() ?? '';
  const region = (isSupportedCountry(country) && normalizeRegion(country, regionRaw)) || regionRaw;
  const postal = (isSupportedCountry(country) && normalizePostalCode(country, postalRaw)) || postalRaw;

  let locality: string;
  if (country === 'CA') {
    locality = [[city, region].filter(Boolean).join(' '), postal].filter(Boolean).join('  ');
  } else {
    const cityRegion = [city, [region, postal].filter(Boolean).join(' ')].filter(Boolean);
    locality = cityRegion.join(', ');
  }
  const lines = [address.line1?.trim(), address.line2?.trim(), locality].filter((l): l is string => !!l);
  const home = (opts.homeCountry ?? DEFAULT_COUNTRY).toUpperCase();
  if (lines.length && country !== home) lines.push(countryName(country) ?? country);
  return lines;
}

/** The address on one line (`, `-separated), e.g. for tables and emails. */
export function formatAddressInline(address: AddressLike | null | undefined, opts: { homeCountry?: string } = {}): string {
  return formatAddressLines(address, opts).join(', ');
}

// ---------------------------------------------------------------------------
// Canadian sales tax (GST / HST / PST / RST / QST)
// ---------------------------------------------------------------------------

export type SalesTaxCode = 'GST' | 'HST' | 'PST' | 'RST' | 'QST';

export type SalesTaxComponent = {
  code: SalesTaxCode;
  name: string;
  /**
   * Rate in basis points (1% = 100). Quebec's QST is 9.975%, i.e. 997.5 bps, the one rate that is
   * not a whole number of basis points; amounts are computed exactly (see `salesTaxBreakdown`).
   */
  rateBps: number;
  /** Short label for document lines, e.g. "HST 13%" or "QST 9.975%". */
  label: string;
};

export type SalesTax = {
  region: CanadianProvince;
  components: SalesTaxComponent[];
  /** Sum of the component rates in basis points (QC: 1497.5). */
  totalRateBps: number;
};

const GST_BPS = 500;

const TAX_NAMES: Record<SalesTaxCode, string> = {
  GST: 'Goods and Services Tax',
  HST: 'Harmonized Sales Tax',
  PST: 'Provincial Sales Tax',
  RST: 'Retail Sales Tax',
  QST: 'Quebec Sales Tax',
};

type RatePeriod = { from: string; rateBps: number };

/**
 * Provincial part of the sales tax, by province. Rates are effective-dated (newest last) so
 * documents dated before a change keep the old rate.
 *
 * Source: Canada Revenue Agency, "GST/HST rates" (canada.ca/en/revenue-agency/services/tax/
 * businesses/topics/gst-hst-businesses/charge-collect-which-rate), checked 2026-10-14: ON 13% HST;
 * NB, NL, PE 15% HST; NS 14% HST since 2025-04-01 (15% before); BC PST 7%; MB RST 7%; SK PST 6%;
 * QC QST 9.975%; AB, NT, NU, YT GST only. Re-check that page when a provincial budget changes a rate.
 */
const PROVINCIAL: Record<CanadianProvince, { code: SalesTaxCode; periods: RatePeriod[] } | null> = {
  AB: null,
  BC: { code: 'PST', periods: [{ from: '0000-01-01', rateBps: 700 }] },
  MB: { code: 'RST', periods: [{ from: '0000-01-01', rateBps: 700 }] },
  NB: { code: 'HST', periods: [{ from: '0000-01-01', rateBps: 1500 }] },
  NL: { code: 'HST', periods: [{ from: '0000-01-01', rateBps: 1500 }] },
  NS: {
    code: 'HST',
    periods: [
      { from: '0000-01-01', rateBps: 1500 },
      { from: '2025-04-01', rateBps: 1400 },
    ],
  },
  NT: null,
  NU: null,
  ON: { code: 'HST', periods: [{ from: '0000-01-01', rateBps: 1300 }] },
  PE: { code: 'HST', periods: [{ from: '0000-01-01', rateBps: 1500 }] },
  QC: { code: 'QST', periods: [{ from: '0000-01-01', rateBps: 997.5 }] },
  SK: { code: 'PST', periods: [{ from: '0000-01-01', rateBps: 600 }] },
  YT: null,
};

/** "13%", "9.975%", "5%". */
export function formatTaxRate(rateBps: number): string {
  return `${Number((rateBps / 100).toFixed(4))}%`;
}

function component(code: SalesTaxCode, rateBps: number): SalesTaxComponent {
  return { code, name: TAX_NAMES[code], rateBps, label: `${code} ${formatTaxRate(rateBps)}` };
}

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Sales tax that applies in a Canadian province or territory on `onDate` (`YYYY-MM-DD`, default
 * today). HST provinces have one component (HST, which includes the federal part); BC, MB, SK and
 * QC have GST plus the provincial tax; AB and the territories have GST only.
 * Accepts a code or a name ("on", "Ontario"). Null for anything that is not a Canadian province.
 */
export function canadianSalesTax(region: string | null | undefined, onDate: string = todayUtc()): SalesTax | null {
  const code = normalizeRegion('CA', region) as CanadianProvince | null;
  if (!code) return null;
  const provincial = PROVINCIAL[code];
  const components: SalesTaxComponent[] = [];
  if (!provincial) {
    components.push(component('GST', GST_BPS));
  } else {
    const period = [...provincial.periods].reverse().find((p) => p.from <= onDate) ?? provincial.periods[0]!;
    if (provincial.code === 'HST') components.push(component('HST', period.rateBps));
    else components.push(component('GST', GST_BPS), component(provincial.code, period.rateBps));
  }
  return { region: code, components, totalRateBps: components.reduce((sum, c) => sum + c.rateBps, 0) };
}

/**
 * Combined rate as whole basis points, for the single `defaultTaxRateBps` workspace setting and
 * the one-rate-per-document tax math. Exact everywhere except Quebec, whose 14.975% rounds to
 * 1498 (use `salesTaxBreakdown` for exact GST + QST amounts). Null outside Canada.
 */
export function defaultTaxRateBpsFor(region: string | null | undefined, onDate?: string): number | null {
  const tax = canadianSalesTax(region, onDate);
  return tax ? Math.round(tax.totalRateBps) : null;
}

/** `amount * rateBps / 10_000` rounded half away from zero, in exact integer arithmetic. */
function taxOn(amountCents: number, rateBps: number): number {
  // Rates have at most one decimal place in bps (997.5), so scale by 10 to stay in integers.
  const num = Math.abs(amountCents) * Math.round(rateBps * 10);
  const den = 100_000;
  const q = Math.floor(num / den);
  const rounded = (num - q * den) * 2 >= den ? q + 1 : q;
  return amountCents < 0 ? -rounded : rounded;
}

export type SalesTaxLine = SalesTaxComponent & { amountCents: number };

/**
 * Tax on a taxable amount, one line per component (each rounded to the cent separately, as GST
 * and QST are reported separately). Null outside Canada.
 */
export function salesTaxBreakdown(
  taxableCents: number,
  region: string | null | undefined,
  onDate?: string,
): { region: CanadianProvince; lines: SalesTaxLine[]; totalCents: number; totalRateBps: number } | null {
  const tax = canadianSalesTax(region, onDate);
  if (!tax) return null;
  const lines = tax.components.map((c) => ({ ...c, amountCents: taxOn(taxableCents, c.rateBps) }));
  return { region: tax.region, lines, totalCents: lines.reduce((s, l) => s + l.amountCents, 0), totalRateBps: tax.totalRateBps };
}
